import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Inject,
    ViewEncapsulation,
    viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
    CommandPaletteEntry,
    CommandPaletteGroup,
    groupCommandPaletteEntries,
} from './command-palette.logic';

export interface CommandPaletteDialogData {
    /** Highlighted list row, e.g. "12 · Paracetamol". Empty when no row is selected. */
    selectionLabel?: string | null;
    filter: (query: string) => CommandPaletteEntry[];
}

/**
 * Page and command search opened by Ctrl/Cmd+K.
 * Arrow keys move the highlight across all sections, Enter picks,
 * Escape is MatDialog's default close.
 */
@Component({
    selector: 'app-command-palette',
    standalone: true,
    templateUrl: './command-palette.component.html',
    styleUrl: './command-palette.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [MatDialogModule, MatIconModule],
})
export class CommandPaletteComponent implements AfterViewInit {
    private readonly _searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
    private readonly _resultList = viewChild<ElementRef<HTMLElement>>('resultList');

    results: CommandPaletteEntry[] = [];
    groups: CommandPaletteGroup[] = [];
    activeIndex = 0;
    /** Highlighted list row captured when the palette opened. */
    selectionLabel: string | null = null;
    /** True when at least one Selected Record action has a number shortcut. */
    hasRecordShortcuts = false;

    constructor(
        private _dialogRef: MatDialogRef<CommandPaletteComponent, CommandPaletteEntry>,
        @Inject(MAT_DIALOG_DATA) private _data: CommandPaletteDialogData,
    ) {
        this.selectionLabel = this._data.selectionLabel?.trim() || null;
        this._setResults(this._data.filter(''));
    }

    ngAfterViewInit(): void {
        setTimeout(() => this._searchInput()?.nativeElement.focus());
    }

    get activeResultId(): string | null {
        return this.results.length ? `command-palette-option-${this.activeIndex}` : null;
    }

    onQueryInput(event: Event): void {
        const value = (event.target as HTMLInputElement).value ?? '';
        this._setResults(this._data.filter(value));
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            this._move(1);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            event.stopPropagation();
            this._move(-1);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.select(this.results[this.activeIndex]);
            return;
        }
        this._runNumberShortcut(event);
    }

    select(entry: CommandPaletteEntry | undefined): void {
        if (!entry || entry.disabled) {
            return;
        }
        this._dialogRef.close(entry);
    }

    private _setResults(results: CommandPaletteEntry[]): void {
        this._numberSelectedRecordActions(results);
        this.results = results;
        this.groups = groupCommandPaletteEntries(results);
        const firstEnabled = results.findIndex((entry) => !entry.disabled);
        this.activeIndex = firstEnabled >= 0 ? firstEnabled : 0;
    }

    /**
     * Number the runnable Selected Record rows 1–9, in the order they appear.
     * A search query rebuilds the list, so the digit always matches the row on screen.
     */
    private _numberSelectedRecordActions(results: CommandPaletteEntry[]): void {
        let next = 1;
        for (const entry of results) {
            if (entry.section === 'Selected Record' && !entry.disabled && next <= 9) {
                entry.shortcut = String(next);
                next += 1;
            } else {
                entry.shortcut = undefined;
            }
        }
        this.hasRecordShortcuts = next > 1;
    }

    /** Empty search: 1–9 runs that Selected Record action. A typed query keeps the digit for filtering. */
    private _runNumberShortcut(event: KeyboardEvent): void {
        if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
            return;
        }
        if (!/^[1-9]$/.test(event.key)) {
            return;
        }
        const query = this._searchInput()?.nativeElement.value ?? '';
        if (query.trim()) {
            return;
        }
        const entry = this.results.find((item) => item.shortcut === event.key && !item.disabled);
        if (!entry) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.select(entry);
    }

    private _move(delta: number): void {
        const count = this.results.length;
        if (!count) {
            return;
        }
        let next = this.activeIndex;
        for (let step = 0; step < count; step++) {
            next = (next + delta + count) % count;
            if (!this.results[next]?.disabled) {
                this.activeIndex = next;
                break;
            }
        }
        setTimeout(() => {
            this._resultList()
                ?.nativeElement.querySelector('.is-active')
                ?.scrollIntoView({ block: 'nearest' });
        });
    }
}
